import { Component, EventEmitter, OnInit, Output, Signal, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
    CeButtonComponent,
    CeIconComponent,
    CeListImports,
    CeNameDisplayComponent,
    CeOverlayService,
    CeSnackbarService,
    CeSpinningLoaderComponent,
    CeTooltipDirective,
    ModalSizeOption,
    TOAST_LOCAL_STORAGE_KEY,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { IamUsersApiServiceObservable, UserTenancyAccessObject } from '@kempoweroyj/ngx-iam-users-api-client';
import {
    IamOrganizationsApiService,
    IamOrganizationsApiServiceObservable,
} from '@kempoweroyj/ngx-iam-organizations-api-client';
import { RelOrganization, SearchApiServiceObservable } from '@kempoweroyj/ngx-search-api-client';
import { CE_SYSTEM_ROLES, CeIfFeatureDirective } from '@kempower-nx-workspace/chargeye-shared';
import { NotificationsManagementService } from '@kempower-nx-workspace/chargeye-notifications-management';

import { LicenseInfoService } from '@kempower-nx-workspace/chargeye-licensing';
import { OrgSwitchDialogComponent } from '../organization-switcher-dialog/org-switcher-dialog.component';
import { tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ImpersonationLocalCacheService } from '../../data-access/impersonation-local-cache/impersonation-local-cache.service';
import {
    GetMyTenancyAccessWithName,
    ImpersonationService,
} from '../../data-access/impersonation-service/impersonation.service';
import { AuthenticationService } from '@kempower-nx-workspace/ce-authentication';
import { UserProfile } from '@kempower-nx-workspace/ce-user-profile';
import { capitalize as _capitalize } from 'lodash-es';
import { LicensingApiServiceObservable } from '@kempoweroyj/ngx-licensing-api-client';
import { SimplifiedOrganizationDetails } from '../../models/simplified-organization-details.model';

interface OrgSwitchList {
    id: string;
    name: string;
    tenantId?: string;
    systemRoles: string[];
    roles: string[];
}

type orgSwitchEventType = 'loading' | 'loaded' | 'close';
const MAX_LIST_ELEMENTS = 4;
type RolesData = {
    roles: string[];
    systemRoles: string[];
} | null;

@Component({
    selector: 'ce-app-org-switch-popover',
    providers: [
        ImpersonationLocalCacheService,
        LicensingApiServiceObservable,
        SearchApiServiceObservable,
        IamOrganizationsApiService,
        IamUsersApiServiceObservable,
        ImpersonationService,
        IamOrganizationsApiServiceObservable,
    ],
    templateUrl: './organization-switch.component.html',
    styleUrls: ['./organization-switch.component.scss'],
    imports: [
        CommonModule,
        CeButtonComponent,
        CeIfFeatureDirective,
        CeNameDisplayComponent,
        CeListImports,
        CeTooltipDirective,
        CeSpinningLoaderComponent,
        CeListImports,
        CeIconComponent,
    ]
})
export class OrganizationSwitchComponent implements OnInit {
    @Output() orgSwitchEvent: EventEmitter<orgSwitchEventType> = new EventEmitter();

    #router = inject(Router);
    overlay = inject(CeOverlayService);
    snackbarService = inject(CeSnackbarService);
    licenseInfoService = inject(LicenseInfoService);
    impersonationService = inject(ImpersonationService);
    authenticationService = inject(AuthenticationService);
    impersonationLocalCache = inject(ImpersonationLocalCacheService);
    iamOrganizationsApiService = inject(IamOrganizationsApiService);
    iamUsersApiServiceObservable = inject(IamUsersApiServiceObservable);
    iamOrganizationsApiServiceObservable = inject(IamOrganizationsApiServiceObservable);
    readonly #notificationsManagement = inject(NotificationsManagementService);

    tokenProfileData!: Signal<UserProfile>;

    isLoading = signal<boolean>(true);
    isSysAdmin = signal<boolean>(false);
    currentRoles = signal<RolesData>(null);
    showSearchBox = signal<boolean>(false);
    isImpersonating = signal<boolean>(false);
    selectedOrganizationId = signal<string>('');
    impersonationUpdating = signal<boolean>(false);

    organisationList!: Signal<SimplifiedOrganizationDetails[] | GetMyTenancyAccessWithName[]>;

    readonly #popupFlagKey = 'showPopupAfterRefresh';

    constructor() {
        const authData = localStorage.getItem('kempower-csms-auth');

        if (authData !== null) {
            const jsonAuthData = JSON.parse(authData);

            this.isSysAdmin.set(
                jsonAuthData.profile?.originalSystemRoles?.includes('systemAdmin') ??
                    jsonAuthData.profile?.systemRoles?.includes('systemAdmin')
            );
            // Sometimes we don't get the impersonated value from the token?
            // Probably means we are not impersonating
            this.tokenProfileData = signal<UserProfile>(jsonAuthData.profile);
            this.authenticationService.profileStore.set(jsonAuthData.profile);

            if (this.tokenProfileData()?.impersonated !== undefined) {
                this.isImpersonating.set(jsonAuthData.profile?.impersonated);
            }

            this.currentRoles.set({
                systemRoles: jsonAuthData.profile?.systemRoles ?? [],
                roles: jsonAuthData.profile?.roles ?? [],
            });
        }

        if (this.isSysAdmin() && this.tokenProfileData() !== undefined) {
            this.organisationList = toSignal(
                this.impersonationService.getOrganisationList$(
                    this.isImpersonating(),
                    this.tokenProfileData().originalTenantId ?? ''
                ),
                { initialValue: null }
            ) as Signal<SimplifiedOrganizationDetails[]>;
        } else {
            // TODO: Add getMyHomeOrganization api endpoint when ready. That way rely less on caching
            this.organisationList = toSignal(this.impersonationService.getTenacyAccess$(), {
                initialValue: null,
            }) as Signal<GetMyTenancyAccessWithName[]>;
        }
    }

    ngOnInit(): void {
        this.orgSwitchEvent.emit('loading');
    }

    /**
     * ToSignal convert obs to signals
     */
    currentLicense = toSignal(this.licenseInfoService.getActiveLicense$());

    organisationName = toSignal(
        this.impersonationService.getOrganisationName$().pipe(
            tap((res) => {
                if (typeof res === 'string') {
                    this.authenticationService.profileStore.update((profileStore) => {
                        if (profileStore !== undefined) {
                            return {
                                ...profileStore,
                                organizationName: res,
                            };
                        }
                        return profileStore;
                    });
                }
            })
        ),
        { initialValue: '' }
    );

    /**
     * Compute signals
     */
    licenseName = computed<string | undefined>(() => {
        const license = this.currentLicense();

        if (typeof license !== 'string' || license.length === 0) return undefined;

        const segment = license.slice(license.lastIndexOf('.') + 1);

        if (segment === 'core') {
            return 'MyKempower';
        }
        return `ChargEye ${_capitalize(segment)}`;
    });

    organisationListComputed = computed<OrgSwitchList[]>(() => {
        if (this.organisationList() === null) {
            return [];
        }
        const orgList = this.organisationList();
        let organizationsList = [...orgList];
        const token = this.tokenProfileData();
        const isImpersonating = this.isImpersonating();
        const recentSelections = this.impersonationLocalCache.getRecentCachedOrganisations();

        if (this.isSysAdmin()) {
            organizationsList = organizationsList.filter(
                (org): org is SimplifiedOrganizationDetails => 'tenantId' in org && org.tenantId !== token.tenantId
            );
        }

        if (!this.isSysAdmin() && isImpersonating) {
            organizationsList = organizationsList.filter(
                (org) => !('targetTenantName' in org) || org.targetTenantName !== this.organisationName()
            );
        }

        let listFiltered = organizationsList.slice(0, MAX_LIST_ELEMENTS).map((org) => {
            if ('targetTenantId' in org) {
                return { ...org, id: org.targetTenantId, name: org.name };
            } else {
                const full = org as SimplifiedOrganizationDetails;
                return { ...full, id: full.tenantId, name: full.name };
            }
        }) as OrgSwitchList[];

        if (recentSelections) {
            Object.entries(recentSelections).forEach(([, orgInCache]) => {
                const cachedOrg = orgInCache as OrgSwitchList;
                const index = listFiltered.findIndex((item: { name: string }) => item?.name === cachedOrg?.name);
                if (cachedOrg.id === this.tokenProfileData().tenantId) {
                    return;
                }
                if (index > -1) {
                    listFiltered.splice(index, 1);
                }
                cachedOrg.tenantId = cachedOrg.id;
                listFiltered.unshift(cachedOrg);
            });
        }
        if (listFiltered.length >= 4) {
            const sliceCount = this.isImpersonating() ? 3 : 4;
            listFiltered = listFiltered.slice(0, sliceCount);
        }
        return listFiltered;
    });

    simplifiedList = computed(() => {
        const orgs: Array<UserTenancyAccessObject | RelOrganization> = this.organisationList();
        const token = this.tokenProfileData();
        if (orgs === null) {
            return [];
        }
        return orgs
            .filter((org) => {
                return 'targetTenantId' in org
                    ? org.targetTenantId !== token.tenantId
                    : org.tenantId !== token.tenantId;
            })
            .map((org) => {
                return {
                    ...org,
                    tenantId: 'targetTenantId' in org ? org.targetTenantId : org.tenantId,
                    tenantName: 'targetTenantName' in org ? org.targetTenantName : org.name,
                    tenantPermission:
                        'targetTenantPermission' in org
                            ? org.targetTenantPermission
                            : 'permission' in org
                              ? org.permission
                              : undefined,
                } as SimplifiedOrganizationDetails;
            });
    });

    roleToolTipMessage = computed(() => {
        if (!this.currentRoles()) {
            return false;
        }

        const roles = this.currentRoles()?.roles;
        const systemRoles = this.currentRoles()?.systemRoles ?? [];

        const systemRoleLabels: string[] = [];
        if (systemRoles?.length > 0) {
            systemRoles.forEach((systemRole) => {
                const roleInfo = CE_SYSTEM_ROLES[systemRole as keyof typeof CE_SYSTEM_ROLES];
                if (roleInfo) {
                    systemRoleLabels.push(roleInfo.label);
                }
            });
        }

        const rolesWithCustomText = roles && roles.length > 0 ? roles.map((str: string) => `${str} (custom)`) : [];

        const allRoles = [...(rolesWithCustomText ?? []), ...(systemRoleLabels || [])];

        if (allRoles.length > 1) {
            return allRoles.join(', ');
        }
        return false;
    });

    roleDisplay = computed(() => {
        if (this.isSysAdmin() && !this.isImpersonating()) {
            return 'Kempower admin';
        }
        if (!this.currentRoles()) {
            return false;
        }
        const currentRoles = this.currentRoles();
        const allRoles = [...(currentRoles?.roles || []), ...(currentRoles?.systemRoles || [])];
        if (allRoles.length === 1) {
            const role = allRoles[0];
            const roleInfo = CE_SYSTEM_ROLES[role as keyof typeof CE_SYSTEM_ROLES];
            return roleInfo?.label ?? role;
        } else {
            const roleCount = allRoles.length;
            return roleCount > 1 ? `${roleCount} roles` : 'No roles';
        }
    });

    /**
     * We should get the home organization name from the api to prevent caching issues
     * However we can use the storage as a temporary solution for the non sys admin users
     */
    homeOrganisationName = computed(() => {
        if (this.isSysAdmin() && this.isImpersonating()) {
            const match = this.simplifiedList().find(
                (item) => item.tenantId === this.tokenProfileData().originalTenantId
            );
            return match?.name ?? '';
        } else {
            // TODO: remove this in the future as there is a new endpoint coming out
            const storedData = localStorage.getItem('chargeye-organization-info');
            if (storedData !== null) {
                const orignalData = JSON.parse(storedData);
                return orignalData.originalTenantName;
            }
        }
        return 'Back to home org';
    });

    hasOrgsToImpersonate = computed(() => {
        return this.simplifiedList().length > 0;
    });

    /**
     * Effects for organization switcher
     */

    emitWhenOrgListLoaded = effect(() => {
        if (this.organisationList() !== null) {
            this.orgSwitchEvent.emit('loaded');
        }
    });

    /**
     * Manage the storage of the orignal management of the tenant data
     * This is to be used in the impersonation flow when navigating between the old
     * and new website.
     * This is to be deprecated in the future
     */
    chargeEyeClassicLocalStoragePatch = effect(() => {
        // TODO: introduce a proper state mgmt service.

        if (this.isImpersonating()) {
            this.impersonationLocalCache.addImpersonatedTenantIdToStorage({
                tenantId: this.tokenProfileData()?.tenantId ?? '',
                tenantName: this.organisationName() ?? '',
            });
        } else {
            localStorage.removeItem('chargeye-organization-info');
            const profile = this.tokenProfileData();
            const tenantId = typeof profile === 'object' && profile !== null ? (profile.tenantId ?? '') : '';
            this.impersonationLocalCache.addOrginalTenantIdToStorage({
                originalTenantId: tenantId,
                originalTenantName: this.organisationName() ?? '',
            });
        }
    });

    showSuccessSnackIfFlagExists = effect(
        () => {
            if (localStorage.getItem(this.#popupFlagKey) && this.organisationName()) {
                this.showSuccessSnackbar();
            }
        },
        { allowSignalWrites: true }
    );

    showSuccessSnackbar() {
        const roleFlashStore = localStorage.getItem(this.#popupFlagKey);
        const rolesFromStorage = roleFlashStore ? JSON.parse(roleFlashStore) : [];
        const rolesLength = rolesFromStorage.length;
        let userRolesText = '';

        if (rolesLength > 1) {
            userRolesText = `${rolesLength} user roles`;
        } else if (rolesLength === 1) {
            userRolesText = `user role ${rolesFromStorage[0]}`;
        }

        const roleSuffix = userRolesText ? ' with ' + userRolesText : '';
        const message = 'Switched to ' + this.organisationName() + roleSuffix;
        localStorage.removeItem(this.#popupFlagKey);
        this.snackbarService.showSnackbar({ message });
    }

    backToRecent(item: { id: string }) {
        this.selectedOrganizationId.set(item.id);
        this.openOrgSwitcherDialog();
    }

    changeSelection(org: UserTenancyAccessObject | RelOrganization) {
        if ('targetTenantId' in org && org.targetTenantId !== undefined) {
            this.selectedOrganizationId.set(org.targetTenantId);
        } else if ('tenantId' in org && org.tenantId !== undefined) {
            this.selectedOrganizationId.set(org.tenantId);
        }

        this.openOrgSwitcherDialog();
    }

    changeToHomeOrganization() {
        this.isLoading.set(true);
        this.orgSwitchEvent.emit('loading');

        const originalTenantId = this.tokenProfileData()?.originalTenantId ?? false;

        if (!originalTenantId) {
            return;
        }

        this.iamOrganizationsApiServiceObservable
            .impersonate({ tenantId: originalTenantId })
            .pipe(
                tap((res) => {
                    this.authenticationService.setTokenInStorage(res.token);
                    this.#reloadAfterOrganizationChanged([]);
                })
            )
            .subscribe({
                next: () => {
                    this.isLoading.set(false);
                },
            });
    }

    openOrgSwitcherDialog() {
        this.orgSwitchEvent.emit('close');
        this.impersonationUpdating.set(true);

        this.overlay
            .openModal(OrgSwitchDialogComponent, {
                size: ModalSizeOption.MEDIUM,
                data: {
                    selectedOrgId: this.selectedOrganizationId(),
                    organizations: this.simplifiedList(),
                    sysAdmin: this.isSysAdmin(),
                },
            })
            .afterClosed()
            .subscribe((status) => {
                this.impersonationUpdating.set(false);

                if (status?.organizationChanged) {
                    this.#reloadAfterOrganizationChanged(status?.roles);
                }
            });
        this.selectedOrganizationId.set('');
    }

    navigateToOrgSettings(): void {
        this.orgSwitchEvent.emit('close');
        this.#router.navigate(['/organization-settings']);
    }

    #reloadAfterOrganizationChanged(roles: string[]) {
        // Remove toast data from localStorage, when user switches organization
        const notificationInLocalStorage = localStorage.getItem(TOAST_LOCAL_STORAGE_KEY);
        if (notificationInLocalStorage !== null) {
            localStorage.removeItem(TOAST_LOCAL_STORAGE_KEY);
        }

        localStorage.setItem(this.#popupFlagKey, JSON.stringify(roles));
        window.location.reload();
        this.isLoading.set(false);

        const authData = localStorage.getItem('kempower-csms-auth');
        if (authData !== null) {
            // Reconnect when organizationInfo changes (impersonation)
            // New connection will be properly scoped to the new tenant
            const jsonAuthData = JSON.parse(authData);
            const currentTenantId = jsonAuthData.profile.tenantId ?? jsonAuthData.profile.originalTenantId;
            if (
                this.#notificationsManagement.connectedTenantId &&
                this.#notificationsManagement.connectedTenantId !== currentTenantId
            ) {
                this.#notificationsManagement.disconnect();
                this.#notificationsManagement.connect();
            }
        }
    }
}
